import React from "react";
import {Button} from "@/components/ui/button";
import {SimpleDialog} from "@/main/react/Dialog/SimpleDialog";
import type {Localizer} from "@/main/react/Types";
import {ClientFactory} from "@/main/common/ClientFactory";
import {navigateToUrl} from "@/main/common/BrowserNavigation";
import {isHandledAuthSessionError} from "@/main/common/AjaxAuthSessionHandler";
import {getSafeToastMessageFromError, logApiFailure} from "@/main/common/ApiUtils";
import {newsletterComposeUrl} from "@/main/react/newsletter/newsletterUrls";
import {toast} from "sonner";
import type {NewsletterSendPostChoiceDTO, NewsletterSendPostResponse} from "@/generated-client";

/**
 * "Send to my subscribers" (newsletter growth work order D5/D7/D8/A9, slice 2b).
 *
 * The state behind the post owner's ⋯ menu item. The choices are read when the menu OPENS, never on mount: a
 * feed mounts one action bar per card, and only the owner's open menu needs them (the same reason the safety
 * items load on open). `GET /ajax/newsletter/send-post/{discussionPublicId}` answers the caller's ACTIVE
 * newsletters, each with the issue already made from this post, if any; a post that is not the caller's public
 * post is a 404, which simply leaves the item out.
 *
 * `POST` never sends: it makes (or finds) a DRAFT issue and the client opens the composer on it. CHOOSE (more than
 * one active newsletter, none named) opens the picker. One request at a time: a double tap would race the
 * `(newsletter, source post)` unique index and the loser is a 500, so the item and every picker row are disabled
 * while a request is out, and a ref refuses a second call inside the same tick.
 */
export type SendPostState = {
    /** `null` until the read answers (or when it failed / 404'd): the item is not shown. */
    choices: NewsletterSendPostChoiceDTO[] | null;
    /** Every active newsletter already has an issue from this post (D8: once per post per newsletter). */
    allSent: boolean;
    pending: boolean;
    pickerOpen: boolean;
    setPickerOpen: (open: boolean) => void;
    /** The menu item's action: straight to the composer with one newsletter, the picker with several. */
    start: () => Promise<void>;
    /** A picker row's action. */
    choose: (newsletterPublicId: string) => Promise<void>;
};

export function useSendPostToNewsletter(
    discussionPublicId: string | null | undefined,
    localizer: Localizer,
    enabled: boolean,
): SendPostState {
    const [choices, setChoices] = React.useState<NewsletterSendPostChoiceDTO[] | null>(null);
    const [pending, setPending] = React.useState(false);
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const inFlightRef = React.useRef(false);
    const requestedRef = React.useRef(false);

    React.useEffect(() => {
        if (!enabled || !discussionPublicId || requestedRef.current) return;
        requestedRef.current = true;
        let cancelled = false;
        (async () => {
            try {
                const response = await ClientFactory.getAjaxNewsletterRestControllerApi().listSendPostChoices(discussionPublicId);
                if (!cancelled) {
                    setChoices(Array.isArray(response?.data) ? response.data : []);
                }
            } catch (err: any) {
                // A 404 is the ordinary "not your public post" answer: the item stays out. Let the next open retry.
                requestedRef.current = false;
                if (cancelled || isHandledAuthSessionError(err)) return;
                logApiFailure("Failed loading send-post choices", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [enabled, discussionPublicId]);

    const handleResponse = React.useCallback((data: NewsletterSendPostResponse | undefined): boolean => {
        if (!data) return false;
        if (data.outcome === "CHOOSE") {
            if (Array.isArray(data.choices)) setChoices(data.choices);
            setPickerOpen(true);
            return true;
        }
        const url = newsletterComposeUrl(data.newsletterSlug, data.issue?.publicId);
        if (!url || !data.issue?.publicId) return false;
        setPickerOpen(false);
        navigateToUrl(url, {source: "newsletter-send-post"});
        return true;
    }, []);

    const send = React.useCallback(async (newsletterPublicId?: string) => {
        if (!discussionPublicId || inFlightRef.current) return;
        inFlightRef.current = true;
        setPending(true);
        let navigating = false;
        try {
            const response = await ClientFactory.getAjaxNewsletterRestControllerApi().sendPostToNewsletter(
                discussionPublicId,
                newsletterPublicId ? {newsletterPublicId} : undefined,
            );
            const data = response?.data;
            const handled = handleResponse(data);
            navigating = handled && data?.outcome !== "CHOOSE";
            if (!handled) {
                toast.error(localizer("newsletter.action.failed"));
            }
        } catch (err: any) {
            if (isHandledAuthSessionError(err)) return;
            logApiFailure("Failed sending a post to a newsletter", err);
            toast.error(getSafeToastMessageFromError(err, localizer("newsletter.action.failed")));
        } finally {
            // While the composer loads, the item stays disabled: a second tap would only race the first.
            if (!navigating) {
                inFlightRef.current = false;
                setPending(false);
            }
        }
    }, [discussionPublicId, handleResponse, localizer]);

    const allSent = choices !== null && choices.length > 0 && choices.every((choice) => Boolean(choice.issuePublicId));

    return {
        choices,
        allSent,
        pending,
        pickerOpen,
        setPickerOpen,
        start: () => send(),
        choose: (newsletterPublicId: string) => send(newsletterPublicId),
    };
}

export type SendPostToNewsletterDialogProps = {
    state: SendPostState;
    localizer: Localizer;
};

/**
 * A9: the picker for an owner with more than one active newsletter. Each row is the confirm; a newsletter this
 * post already went to is shown disabled with the "Sent to subscribers" state, never re-offered (D8).
 */
export function SendPostToNewsletterDialog({state, localizer}: SendPostToNewsletterDialogProps) {
    const {choices, pending, pickerOpen, setPickerOpen, choose} = state;
    return (
        <SimpleDialog
            open={pickerOpen}
            onOpenChange={(nextOpen) => {
                if (!pending) setPickerOpen(nextOpen);
            }}
            title={localizer("newsletter.send_post.pick_title")}
            width="sm"
            dataPlaywrightId="newsletter-send-post-picker"
            footer={
                <div className="flex justify-end">
                    <Button type="button" variant="outline" onClick={() => setPickerOpen(false)} disabled={pending}>
                        {localizer("literal.cancel")}
                    </Button>
                </div>
            }
        >
            <ul className="flex flex-col gap-2">
                {(choices ?? []).map((choice) => {
                    const sent = Boolean(choice.issuePublicId);
                    return (
                        <li key={choice.newsletterPublicId}>
                            <Button
                                type="button"
                                variant="outline"
                                className="h-auto min-h-11 w-full justify-between gap-3 whitespace-normal text-left"
                                disabled={pending || sent}
                                onClick={() => void choose(choice.newsletterPublicId)}
                                data-playwright-id="newsletter-send-post-choice"
                                data-newsletter-public-id={choice.newsletterPublicId}
                            >
                                <span className="min-w-0 truncate">{choice.name}</span>
                                {sent && (
                                    <span className="shrink-0 text-xs text-neutral-text">
                                        {localizer("newsletter.send_post.sent")}
                                    </span>
                                )}
                            </Button>
                        </li>
                    );
                })}
            </ul>
        </SimpleDialog>
    );
}
